import { Link } from 'react-router-dom';

import type { PropFirm } from '../../types';
import { propFirmLogos } from '../../data/propFirmLogos';
import { formatCurrency } from '../../utils/formatCurrency';
import PromoCTAButtonBestFirms from '../PromoCTAButtonBestFirms';
import PromoCTAButtonHomepage from '../PromoCTAButtonHomepage';

type Variant = 'compact' | 'full';
type CTAContext = 'homepage' | 'bestFirms';
type HighlightTone = 'gold' | 'green' | 'blue';

type DiscountData = {
  code?: string | null;
  percent?: number | null;
  fixedPrice?: number | null;
  validUntil?: string | null;
  validUntilRemark?: string | null;
  firstPurchaseOnly?: boolean;
};

export interface PropFirmRowCardProps {
  propFirm: PropFirm;
  price: PropFirm['pricing'][0];
  variant?: Variant;
  isFeatured?: boolean;
  isBestDeal?: boolean;
  ctaContext?: CTAContext;
  visibleColumnKeys?: string[];
  highlightLabel?: string;
  highlightTone?: HighlightTone;
}

function toneClasses(tone: HighlightTone) {
  if (tone === 'green') return 'border-green-400 shadow-[0_0_0_1px_rgba(34,197,94,0.4)]';
  if (tone === 'blue') return 'border-blue-400 shadow-[0_0_0_1px_rgba(59,130,246,0.4)]';
  return 'border-yellow-400 shadow-[0_0_0_1px_rgba(250,204,21,0.45)]';
}

export default function PropFirmRowCard({
  propFirm,
  price,
  variant = 'full',
  isFeatured = false,
  isBestDeal = false,
  ctaContext = 'bestFirms',
  visibleColumnKeys,
  highlightLabel,
  highlightTone = 'gold',
}: PropFirmRowCardProps) {
  const hasCol = (key: string) => !visibleColumnKeys || visibleColumnKeys.includes(key);
  const showAnyMetric = hasCol('maxDrawdown') || hasCol('dailyDrawdown') || hasCol('payout');
  const showLeverage = variant === 'full' && showAnyMetric;

  const logo = propFirm.logo || propFirmLogos[propFirm.id]?.logo || '/assets/logos/default-logo.svg';

  const discount: DiscountData | undefined = (price as any)?.bestDiscount || (propFirm as any)?.discounts?.[0];
  const originalFee = typeof price.fee === 'number' ? price.fee : 0;

  const discountedFeeRaw =
    typeof discount?.fixedPrice === 'number'
      ? discount.fixedPrice
      : typeof discount?.percent === 'number'
        ? originalFee * (1 - discount.percent / 100)
        : originalFee;

  const discountedFee = Math.round(discountedFeeRaw * 100) / 100;
  const hasDiscount = discountedFee + 0.00001 < originalFee;

  const savings = Math.max(0, originalFee - discountedFee);
  const savingsDollars = Math.floor(savings);
  const savingsPercent =
    typeof discount?.percent === 'number'
      ? Math.round(discount.percent)
      : originalFee > 0
        ? Math.round((savings / originalFee) * 100)
        : 0;

  const maxDD = typeof (price as any).maxDrawdownPercent === 'number' ? (price as any).maxDrawdownPercent : null;
  const dailyDD =
    typeof (price as any).dailyDrawdownPercent === 'number'
      ? (price as any).dailyDrawdownPercent
      : typeof (price as any).maxDailyDrawdownPercent === 'number'
        ? (price as any).maxDailyDrawdownPercent
        : null;

  const payoutSchedule = (price as any).payout || propFirm.features?.payout || '-';
  const leverage = (price as any).leverage || propFirm.features?.leverage || '-';

  const isHighlighted = !!highlightLabel;

  const baseClasses = 'rounded-2xl border p-4 sm:p-5 text-yellow-100 shadow-md hover:shadow-xl transition-all';
  const featuredClasses = isFeatured
    ? 'border-yellow-400/70 bg-gradient-to-br from-yellow-500/10 via-gray-900 to-gray-950 shadow-[0_0_24px_rgba(250,204,21,0.12)]'
    : 'border-gray-800 bg-gradient-to-br from-[#161616] to-[#111]';

  const highlightClasses = isHighlighted ? `border-2 ${toneClasses(highlightTone)}` : '';

  return (
    <div id={`firm-row-${propFirm.id}`} className={`${baseClasses} ${featuredClasses} ${highlightClasses}`}>
      <div className="flex flex-col gap-4 md:flex-row md:items-center">
        <div className="flex items-center gap-3 min-w-0 md:w-[280px]">
          <Link to={`/reviews/${propFirm.id}`} className="w-12 h-12 bg-black/60 rounded-lg p-2 flex-shrink-0">
            <img src={logo} alt={`${propFirm.name} logo`} className="w-full h-full object-contain" />
          </Link>

          <div className="min-w-0 flex-1">
            <Link to={`/reviews/${propFirm.id}`} className="font-semibold text-yellow-300 hover:underline block truncate">
              {propFirm.name}
            </Link>

            <div className="mt-1 flex flex-wrap items-center gap-2">
              {isBestDeal && (
                <span className="text-xs text-yellow-300 bg-yellow-700/20 rounded px-2 py-0.5">🔥 Best Deal</span>
              )}
              {isFeatured && (
                <span className="text-xs text-black bg-yellow-400 rounded px-2 py-0.5 font-semibold">Editor&apos;s Pick</span>
              )}
              {isHighlighted && (
                <span className="text-xs text-black bg-yellow-400 rounded px-2 py-0.5 font-semibold">
                  {highlightLabel}
                </span>
              )}
            </div>
          </div>
        </div>

        {showAnyMetric && (
          <div className="flex-1">
            <div
              className={`grid gap-3 ${showLeverage ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3'}`}
            >
              {hasCol('maxDrawdown') && (
                <div className="rounded-xl bg-black/20 border border-white/5 p-3 text-center">
                  <div className="text-lg font-bold text-green-400">{typeof maxDD === 'number' ? `${maxDD}%` : '-'}</div>
                  <div className="text-xs text-gray-400 mt-1">Max Drawdown</div>
                </div>
              )}

              {hasCol('dailyDrawdown') && (
                <div className="rounded-xl bg-black/20 border border-white/5 p-3 text-center">
                  <div className="text-lg font-bold text-blue-400">{typeof dailyDD === 'number' ? `${dailyDD}%` : '-'}</div>
                  <div className="text-xs text-gray-400 mt-1">Daily Drawdown</div>
                </div>
              )}

              {hasCol('payout') && (
                <div className="rounded-xl bg-black/20 border border-white/5 p-3 text-center">
                  <div className="text-lg font-bold text-yellow-400 capitalize">{payoutSchedule}</div>
                  <div className="text-xs text-gray-400 mt-1">Payout</div>
                </div>
              )}

              {showLeverage && (
                <div className="rounded-xl bg-black/20 border border-white/5 p-3 text-center">
                  <div className="text-lg font-bold text-yellow-300">{leverage || '-'}</div>
                  <div className="text-xs text-gray-400 mt-1">Leverage</div>
                </div>
              )}
            </div>
          </div>
        )}

        {(hasCol('fee') || hasCol('action')) && (
          <div className="md:w-[260px] rounded-xl bg-black/20 border border-white/5 p-3">
            {hasCol('fee') && (
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-300">{formatCurrency(discountedFee)}</div>
                {hasDiscount ? (
                  <>
                    <div className="text-sm text-gray-400 mt-1">
                      <s>{formatCurrency(originalFee)}</s>
                    </div>
                    <div className="text-xs text-yellow-300 mt-1">Save ${savingsDollars.toLocaleString()} ({savingsPercent}%)</div>
                  </>
                ) : (
                  <div className="text-xs text-gray-400 mt-1">Final Price</div>
                )}
              </div>
            )}

            {hasCol('action') && (
              <div className="mt-3 flex justify-center">
                {ctaContext === 'homepage' ? (
                  <PromoCTAButtonHomepage
                    firm={{
                      id: propFirm.id,
                      affiliateLink: propFirm.affiliateLink,
                      fee: originalFee,
                      discounts: (propFirm as any).discounts,
                    }}
                    isBestValue={isBestDeal}
                  />
                ) : (
                  <PromoCTAButtonBestFirms
                    firmId={propFirm.id}
                    affiliateLink={`/go/${propFirm.id}`}
                    originalFee={originalFee}
                    discountedFee={discountedFee}
                    discount={{
                      code: discount?.code || undefined,
                      percent: typeof discount?.percent === 'number' ? discount.percent : undefined,
                      validUntilRemark: discount?.validUntilRemark || undefined,
                      firstPurchaseOnly: discount?.firstPurchaseOnly,
                    }}
                    isBestDeal={isBestDeal}
                    size={variant === 'compact' ? 'sm' : 'md'}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
